/**
 * ReleaseContent — /crm/settings/release-content. Admin editor for the wording on the
 * medical-records release the patient e-signs (Phase E follow-up).
 *
 * The built-in defaults ship in code; an admin override is stored per-network
 * and SNAPSHOTTED onto each release request at mint, so saving here only changes
 * releases created AFTER the save — a patient mid-signing keeps the wording they
 * were sent. "Reset to defaults" clears the override.
 *
 * Modeled on the AI Prompts editor — same primary chrome, inline notice/error
 * banners, admin gate, and Save / Reset lifecycle.
 */
import { useCallback, useEffect, useState } from 'react';
import { FileSignature, Loader2, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import api from '../../../services/api';
import { useIsCRMAdmin } from '../../lib';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

interface ReleaseTo { name: string; address: string; phone: string; }
interface ReleaseContent {
  title: string;
  intro: string;
  provider_instruction: string;
  records_requested: string;
  release_to: ReleaseTo;
  authorization_paragraphs: string[];
  consent_wording: string;
}

const inputCls =
  'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500';
const labelCls = 'mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300';

/** Stable empty shape so the form renders before the first load resolves. */
const EMPTY: ReleaseContent = {
  title: '', intro: '', provider_instruction: '', records_requested: '',
  release_to: { name: '', address: '', phone: '' },
  authorization_paragraphs: [''],
  consent_wording: '',
};

export default function ReleaseContent() {
  const isAdmin = useIsCRMAdmin();
  const [form, setForm] = useState<ReleaseContent>(EMPTY);
  const [customized, setCustomized] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/crm/release-content');
      const c: ReleaseContent = res.data?.content;
      if (c) {
        setForm({
          ...c,
          release_to: { ...EMPTY.release_to, ...(c.release_to || {}) },
          authorization_paragraphs: c.authorization_paragraphs?.length ? c.authorization_paragraphs : [''],
        });
      }
      setCustomized(Boolean(res.data?.customized));
      setDirty(false);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = <K extends keyof ReleaseContent>(k: K, v: ReleaseContent[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };
  const setReleaseTo = (k: keyof ReleaseTo, v: string) => {
    setForm((f) => ({ ...f, release_to: { ...f.release_to, [k]: v } }));
    setDirty(true);
  };
  const setParagraph = (i: number, v: string) => {
    setForm((f) => ({ ...f, authorization_paragraphs: f.authorization_paragraphs.map((p, idx) => (idx === i ? v : p)) }));
    setDirty(true);
  };
  const addParagraph = () => {
    setForm((f) => (f.authorization_paragraphs.length >= 8 ? f : { ...f, authorization_paragraphs: [...f.authorization_paragraphs, ''] }));
    setDirty(true);
  };
  const removeParagraph = (i: number) => {
    setForm((f) => (f.authorization_paragraphs.length <= 1 ? f : { ...f, authorization_paragraphs: f.authorization_paragraphs.filter((_, idx) => idx !== i) }));
    setDirty(true);
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.put('/api/crm/release-content', {
        ...form,
        authorization_paragraphs: form.authorization_paragraphs.map((p) => p.trim()).filter(Boolean),
      });
      const c: ReleaseContent = res.data?.content;
      if (c) {
        setForm({
          ...c,
          release_to: { ...EMPTY.release_to, ...(c.release_to || {}) },
          authorization_paragraphs: c.authorization_paragraphs?.length ? c.authorization_paragraphs : [''],
        });
      }
      setCustomized(Boolean(res.data?.customized));
      setDirty(false);
      setNotice('Saved. New release requests will use this wording — anyone mid-signing keeps the version they were sent.');
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (resetting) return;
    setResetting(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.delete('/api/crm/release-content');
      const c: ReleaseContent = res.data?.content;
      if (c) {
        setForm({
          ...c,
          release_to: { ...EMPTY.release_to, ...(c.release_to || {}) },
          authorization_paragraphs: c.authorization_paragraphs?.length ? c.authorization_paragraphs : [''],
        });
      }
      setCustomized(false);
      setDirty(false);
      setNotice('Reverted to the built-in default wording.');
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setResetting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Editing the records-release wording is available to CRM admins only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      {/* Header card */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="inline-flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
              <FileSignature className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              Records release wording
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
              The text on the medical-records release patients review and e-sign. Edit it here to update every
              future release. {customized ? 'You are using customized wording.' : 'You are on the built-in default wording.'}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={reset}
              disabled={resetting || saving || !customized}
              title={customized ? 'Revert to the built-in default wording' : 'Already on the built-in default'}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Reset to default
            </button>
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </button>
          </div>
        </div>

        {notice && (
          <div className="mt-3 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
            {notice}
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          Keep the wording concise — the signed release is designed to fit on a single page. Very long edits
          may push it onto a second page. You can use <code className="font-mono">{'{{wcr_fax}}'}</code> (our
          return fax) and <code className="font-mono">{'{{today}}'}</code> (the date) anywhere in the text.
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* Headings */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Headings</h3>
            <div className="mt-3 space-y-4">
              <div>
                <label className={labelCls}>Document title</label>
                <input className={inputCls} value={form.title} onChange={(e) => setField('title', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Intro line</label>
                <input className={inputCls} value={form.intro} onChange={(e) => setField('intro', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Provider instruction</label>
                <textarea className={`${inputCls} leading-relaxed`} rows={2} value={form.provider_instruction} onChange={(e) => setField('provider_instruction', e.target.value)} />
                <p className="mt-1 text-xs text-gray-400">Shown right above the office(s) the patient names.</p>
              </div>
              <div>
                <label className={labelCls}>Records requested</label>
                <textarea className={`${inputCls} leading-relaxed`} rows={2} value={form.records_requested} onChange={(e) => setField('records_requested', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Release-to org */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Release information to</h3>
            <p className="mt-1 text-xs text-gray-400">Your organization, as it appears on the release. The return fax line is set automatically.</p>
            <div className="mt-3 space-y-4">
              <div>
                <label className={labelCls}>Organization name</label>
                <input className={inputCls} value={form.release_to.name} onChange={(e) => setReleaseTo('name', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Address</label>
                <input className={inputCls} value={form.release_to.address} onChange={(e) => setReleaseTo('address', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input className={inputCls} value={form.release_to.phone} onChange={(e) => setReleaseTo('phone', e.target.value)} />
              </div>
            </div>
          </div>

          {/* Authorization paragraphs */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Authorization paragraphs</h3>
              {form.authorization_paragraphs.length < 8 && (
                <button onClick={addParagraph} className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline dark:text-primary-400">
                  <Plus className="h-3.5 w-3.5" /> Add paragraph
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-400">The HIPAA authorization the patient grants — include your revocation contact and expiry here.</p>
            <div className="mt-3 space-y-3">
              {form.authorization_paragraphs.map((p, i) => (
                <div key={i}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Paragraph {i + 1}</span>
                    {form.authorization_paragraphs.length > 1 && (
                      <button onClick={() => removeParagraph(i)} className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500" aria-label={`Remove paragraph ${i + 1}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Remove
                      </button>
                    )}
                  </div>
                  <textarea className={`${inputCls} leading-relaxed`} rows={5} value={p} onChange={(e) => setParagraph(i, e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          {/* Consent statement */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">E-signature consent statement</h3>
            <p className="mt-1 text-xs text-gray-400">The checkbox the patient confirms above their signature.</p>
            <textarea className={`${inputCls} mt-3 leading-relaxed`} rows={3} value={form.consent_wording} onChange={(e) => setField('consent_wording', e.target.value)} />
          </div>
        </div>
      )}
    </div>
  );
}
